import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import LocalidadSearch from '../LocalidadSearch';
import RutaSearch from '../RutaSearch';
import proveedorService, { type Proveedor } from '../../services/proveedorService';
import estrategiaService from '../../services/estrategiaService';
import type { ProveedorInicialDesdePrecarga } from '../../utils/compraFacturaPrecarga';
import { normalizeCuitForStorage } from '../../utils/formatters';

const TIPO_IVA_OPCIONES: { value: string; label: string }[] = [
  { value: 'I', label: 'Responsable Inscripto' },
  { value: 'M', label: 'Monotributo' },
  { value: 'E', label: 'Exento' },
  { value: 'P', label: 'Presupuestos' },
];

const TIPO_PROVEEDOR_OPCIONES: { value: 'C' | 'G'; label: string }[] = [
  { value: 'C', label: 'Compras' },
  { value: 'G', label: 'Gastos' },
];

const extractApiError = (e: unknown): string => {
  const err = e as { response?: { data?: unknown }; message?: string };
  const data = err?.response?.data;
  if (!data) return err?.message ?? 'Error al guardar el proveedor';
  if (typeof data === 'string') return data;
  const d = data as Record<string, unknown>;
  if (d.message) return String(d.message);
  if (d.errors && typeof d.errors === 'object') {
    return Object.values(d.errors as Record<string, string[]>)
      .flat()
      .join(' | ');
  }
  return JSON.stringify(data).substring(0, 300);
};

interface LocalidadSel {
  codigoCiudad: number;
  nombreCiudad?: string;
  nombreProvincia?: string;
}

interface RutaSel {
  codigoRuta: number;
  nombreRuta?: string;
}

interface Props {
  open: boolean;
  initialData: ProveedorInicialDesdePrecarga;
  onClose: () => void;
  onCreated: (proveedor: Proveedor) => void;
}

const NuevoProveedorDesdePrecargaDialog: React.FC<Props> = ({
  open,
  initialData,
  onClose,
  onCreated,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [loadingInit, setLoadingInit] = useState(false);
  const [nroProveedor, setNroProveedor] = useState(0);
  const [nroCuenta, setNroCuenta] = useState<number | null>(null);
  const [nombre, setNombre] = useState('');
  const [cuit, setCuit] = useState('');
  const [tipoIva, setTipoIva] = useState('I');
  const [tipoProveedor, setTipoProveedor] = useState<'C' | 'G'>('C');
  const [direccion, setDireccion] = useState('');
  const [localidad, setLocalidad] = useState<LocalidadSel | null>(null);
  const [localidadOpen, setLocalidadOpen] = useState(false);
  const [ruta, setRuta] = useState<RutaSel | null>(null);
  const [rutaOpen, setRutaOpen] = useState(false);

  const resetForm = useCallback(() => {
    setNombre(initialData.nombre ?? '');
    setCuit(initialData.cuit ?? '');
    setTipoIva(initialData.tipoIva || 'I');
    setTipoProveedor(initialData.tipoProveedor || 'C');
    setDireccion(initialData.domicilio ?? '');
    setLocalidad(null);
    setRuta(null);
  }, [initialData]);

  useEffect(() => {
    if (!open) return;
    resetForm();
    setLoadingInit(true);
    void (async () => {
      try {
        const [proximo, estrategia] = await Promise.all([
          proveedorService.getProximoNumero(),
          estrategiaService.getEstrategia(1).catch(() => null),
        ]);
        setNroProveedor(Number(proximo) || 0);
        const cuenta = estrategia?.proveedores;
        setNroCuenta(cuenta != null && Number(cuenta) > 0 ? Number(cuenta) : null);
      } catch {
        enqueueSnackbar('No se pudo obtener el próximo número de proveedor', { variant: 'warning' });
        setNroProveedor(0);
        setNroCuenta(null);
      } finally {
        setLoadingInit(false);
      }
    })();
  }, [open, resetForm, enqueueSnackbar]);

  const handleSubmit = async () => {
    const nombreTrim = nombre.trim();
    if (!nombreTrim) {
      enqueueSnackbar('Ingresá el nombre del proveedor', { variant: 'warning' });
      return;
    }
    if (!tipoIva.trim()) {
      enqueueSnackbar('Seleccioná el tipo de IVA', { variant: 'warning' });
      return;
    }
    if (!tipoProveedor) {
      enqueueSnackbar('Seleccioná el tipo de proveedor', { variant: 'warning' });
      return;
    }
    if (!direccion.trim()) {
      enqueueSnackbar('Ingresá la dirección', { variant: 'warning' });
      return;
    }
    if (!localidad?.codigoCiudad || localidad.codigoCiudad <= 0) {
      enqueueSnackbar('Seleccioná la ciudad', { variant: 'warning' });
      return;
    }
    if (!ruta?.codigoRuta || ruta.codigoRuta < 1) {
      enqueueSnackbar('Seleccioná la ruta/zona', { variant: 'warning' });
      return;
    }
    if (!nroProveedor || nroProveedor <= 0) {
      enqueueSnackbar('No hay número de proveedor disponible. Reintentá o cargá el proveedor desde el maestro.', {
        variant: 'warning',
      });
      return;
    }

    const cuitParaGuardar = normalizeCuitForStorage(cuit);
    const hoy = new Date().toISOString().split('T')[0];

    setSaving(true);
    try {
      const payload = {
        nroProveedor,
        nombre: nombreTrim,
        direccion: direccion.trim(),
        codigoCiudad: localidad.codigoCiudad,
        cuit: cuitParaGuardar === '' ? null : cuitParaGuardar,
        tipoIva: tipoIva.trim().toUpperCase(),
        tipoProveedor,
        condVta: 'CTA.CTE.',
        activo: 'A',
        codigoRuta: ruta.codigoRuta,
        nroCuenta: nroCuenta && nroCuenta > 0 ? nroCuenta : undefined,
        fechaAlta: new Date(`${hoy}T00:00:00Z`).toISOString(),
        esPersHumSucIndig: 0,
        Nombre: nombreTrim,
        Direccion: direccion.trim(),
        TipoProveedor: tipoProveedor,
      };

      const creado = await proveedorService.createProveedor(payload);
      const nro = Number(creado.nroProveedor) || nroProveedor;
      let completo: Proveedor = creado;
      try {
        completo = await proveedorService.getProveedor(nro);
      } catch {
        /* usar respuesta del alta */
      }
      enqueueSnackbar(`Proveedor #${nro} creado`, { variant: 'success' });
      onCreated(completo);
      onClose();
    } catch (e) {
      enqueueSnackbar(extractApiError(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const localidadLabel = localidad
    ? [localidad.nombreCiudad, localidad.nombreProvincia].filter(Boolean).join(', ')
    : '';

  const rutaLabel = ruta
    ? `${ruta.codigoRuta}${ruta.nombreRuta?.trim() ? ` - ${ruta.nombreRuta.trim()}` : ''}`
    : '';

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Crear proveedor desde PDF</DialogTitle>
        <DialogContent dividers>
          {loadingInit ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Nº de proveedor asignado: <strong>{nroProveedor > 0 ? nroProveedor : '—'}</strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Nombre"
                  required
                  fullWidth
                  size="small"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="CUIT"
                  fullWidth
                  size="small"
                  value={cuit}
                  onChange={(e) => setCuit(e.target.value)}
                  inputProps={{ inputMode: 'numeric', maxLength: 13 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tipo IVA"
                  required
                  fullWidth
                  size="small"
                  value={tipoIva}
                  onChange={(e) => setTipoIva(e.target.value)}
                >
                  {TIPO_IVA_OPCIONES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Tipo de proveedor"
                  required
                  fullWidth
                  size="small"
                  value={tipoProveedor}
                  onChange={(e) => setTipoProveedor(e.target.value as 'C' | 'G')}
                >
                  {TIPO_PROVEEDOR_OPCIONES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Dirección"
                  required
                  fullWidth
                  size="small"
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Ciudad"
                  required
                  fullWidth
                  size="small"
                  value={localidadLabel}
                  placeholder="Buscar localidad…"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => setLocalidadOpen(true)}
                          aria-label="Buscar ciudad"
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Ruta / zona"
                  required
                  fullWidth
                  size="small"
                  value={rutaLabel}
                  placeholder="Buscar ruta…"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => setRutaOpen(true)}
                          aria-label="Buscar ruta"
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleSubmit()} disabled={saving || loadingInit}>
            {saving ? 'Guardando…' : 'Crear y seleccionar'}
          </Button>
        </DialogActions>
      </Dialog>

      <LocalidadSearch
        open={localidadOpen}
        onClose={() => setLocalidadOpen(false)}
        onSelect={(loc) => {
          setLocalidad({
            codigoCiudad: loc.codigoCiudad,
            nombreCiudad: loc.nombreCiudad,
            nombreProvincia: loc.nombreProvincia,
          });
          setLocalidadOpen(false);
        }}
      />

      <RutaSearch
        open={rutaOpen}
        onClose={() => setRutaOpen(false)}
        onSelect={(r) => {
          if (!r.codigoRuta || r.codigoRuta < 1) return;
          setRuta({
            codigoRuta: r.codigoRuta,
            nombreRuta: r.nombreRuta,
          });
          setRutaOpen(false);
        }}
      />
    </>
  );
};

export default NuevoProveedorDesdePrecargaDialog;
